// Shared consent + analytics module for the Mutualys forms.
// Owns the consent state machine, the GA / Meta Pixel loaders, and a generic
// GA event sender. Each form keeps its own consent-banner UI (markup/styles)
// and its own form-specific event wrappers, but routes through here so the
// consent gating, script loading, and AJAX transport never drift apart.

const MUTUALYS_CONSENT_STORAGE_KEY = 'mutualys_cookie_consent';
let mutualysConsentChoiceMemory = null;
let mutualysWebsiteEmbedConsentBridgeReady = false;
let mutualysAnonymousSessionId = null;
let mutualysAnonymousContext = {};

export const isConsentExcludedWebsiteEmbed = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return (urlParams.get('utm_source') || '').toLowerCase() === 'website';
};

export const normalizeConsentChoice = (choice) => {
  const normalized = (choice || '').toString().trim().toLowerCase();

  if (['accepted', 'accept', 'granted', 'true', '1', 'yes'].includes(normalized)) {
    return 'accepted';
  }

  if (['refused', 'rejected', 'denied', 'false', '0', 'no'].includes(normalized)) {
    return 'refused';
  }

  return null;
};

export const getWebsiteEmbedConsentChoice = () => {
  if (!isConsentExcludedWebsiteEmbed()) return null;

  const urlParams = new URLSearchParams(window.location.search);
  return normalizeConsentChoice(
    urlParams.get('mutualys_tracking_consent')
    || urlParams.get('tracking_consent')
    || urlParams.get('cookie_consent')
  );
};

export const getStoredConsentChoice = () => {
  if (isConsentExcludedWebsiteEmbed()) {
    return mutualysConsentChoiceMemory || getWebsiteEmbedConsentChoice();
  }

  try {
    return window.localStorage.getItem(MUTUALYS_CONSENT_STORAGE_KEY) || mutualysConsentChoiceMemory;
  } catch (error) {
    return mutualysConsentChoiceMemory;
  }
};

export const setStoredConsentChoice = (choice) => {
  mutualysConsentChoiceMemory = choice;

  try {
    window.localStorage.setItem(MUTUALYS_CONSENT_STORAGE_KEY, choice);
  } catch (error) {
    // Keep consent controls usable even when storage is unavailable.
  }
};

export const hasTrackingConsent = () => (
  getStoredConsentChoice() === 'accepted'
  && !window.mutualysTrackingDisabled
);

const readTrackingValue = (source, keys) => {
  if (!source) return '';

  for (const key of keys) {
    if (typeof source.get === 'function') {
      const value = source.get(key);
      if (value) return value;
    } else if (Object.prototype.hasOwnProperty.call(source, key) && source[key]) {
      return source[key];
    }
  }

  return '';
};

const normalizeAdvancedMatchingEmail = (value) => (
  String(value || '').trim().toLowerCase()
);

const normalizeAdvancedMatchingPhone = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (raw.trim().startsWith('+')) return digits;
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.length === 10 && digits.startsWith('0')) return `33${digits.slice(1)}`;

  return digits;
};

export const buildMetaAdvancedMatchingData = (source) => {
  const email = normalizeAdvancedMatchingEmail(readTrackingValue(source, [
    'contact[email]',
    'email',
  ]));
  const phone = normalizeAdvancedMatchingPhone(readTrackingValue(source, [
    'contact[phones][0]',
    'phone',
    'telephone',
    'tel',
  ]));

  return Object.assign(
    {},
    email ? { em: email } : {},
    phone ? { ph: phone } : {},
  );
};

export const applyMetaAdvancedMatching = (source) => {
  const config = window.mutualysTrackingConfig?.meta;
  const matchingData = buildMetaAdvancedMatchingData(source);

  if (
    !hasTrackingConsent()
    || !config?.pixelId
    || typeof window.fbq !== 'function'
    || Object.keys(matchingData).length === 0
  ) {
    return matchingData;
  }

  window.fbq('init', config.pixelId, matchingData);
  return matchingData;
};

export const disableTrackingForCurrentPage = () => {
  window.mutualysTrackingDisabled = true;
  window.mutualysGAClientId = null;

  const gaMeasurementId = window.mutualysTrackingConfig?.ga?.measurementId;
  if (gaMeasurementId) {
    window[`ga-disable-${gaMeasurementId}`] = true;
  }

  if (typeof window.fbq === 'function') {
    window.fbq('consent', 'revoke');
  }

  document
    .querySelectorAll('input[name="ga_client_id"]')
    .forEach(field => field.remove());
};

export const loadScriptOnce = (id, src) => new Promise((resolve, reject) => {
  const existing = document.getElementById(id);
  if (existing) {
    if (
      existing.dataset.loaded === '1'
      || existing.readyState === 'complete'
      || (src.includes('googletagmanager.com/gtag/js') && typeof window.gtag === 'function')
    ) {
      resolve();
      return;
    }
    existing.addEventListener('load', resolve, { once: true });
    existing.addEventListener('error', reject, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  script.addEventListener('load', () => {
    script.dataset.loaded = '1';
    resolve();
  }, { once: true });
  script.addEventListener('error', reject, { once: true });
  document.head.appendChild(script);
});

export const loadGoogleAnalyticsAfterConsent = () => {
  const config = window.mutualysTrackingConfig?.ga;
  if (!hasTrackingConsent() || !config?.measurementId) return;

  window.mutualysTrackingDisabled = false;
  window[`ga-disable-${config.measurementId}`] = false;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  if (!window.mutualysGoogleAnalyticsLoaded) {
    window.mutualysGoogleAnalyticsLoaded = true;
    window.gtag('js', new Date());
    window.gtag('config', config.measurementId, {
      page_title: 'Mutualys Form',
      page_location: window.location.href
    });
  }

  loadScriptOnce(
    'mutualys-google-analytics',
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.measurementId)}`
  ).then(() => {
    window.gtag('get', config.measurementId, 'client_id', (clientId) => {
      if (!clientId) return;

      window.mutualysGAClientId = clientId;
      const form = document.querySelector('.mutualys-form');
      const existingField = form?.querySelector('input[name="ga_client_id"]');
      if (existingField) {
        existingField.value = clientId;
      } else if (form) {
        const clientIdField = document.createElement('input');
        clientIdField.type = 'hidden';
        clientIdField.name = 'ga_client_id';
        clientIdField.value = clientId;
        form.appendChild(clientIdField);
      }
      if (typeof fetch === 'undefined') return;

      const urlParams = new URLSearchParams(window.location.search);
      fetch('/wp-admin/admin-ajax.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'mutualys_ga_track',
          project_id: config.projectId || '',
          client_id: clientId,
          event_name: 'page_view',
          parameters: JSON.stringify({
            page_location: window.location.href,
            page_title: document.title,
            utm_source: urlParams.get('utm_source') || '',
            utm_medium: urlParams.get('utm_medium') || '',
            utm_campaign: urlParams.get('utm_campaign') || ''
          }),
          nonce: config.nonce || ''
        })
      }).catch(error => console.log('GA tracking error:', error));
    });
  }).catch(error => console.log('GA script load error:', error));
};

export const loadMetaPixelAfterConsent = () => {
  const config = window.mutualysTrackingConfig?.meta;
  if (!hasTrackingConsent() || !config?.pixelId) return;

  window.mutualysTrackingDisabled = false;
  if (window.mutualysMetaPixelLoaded) {
    if (typeof window.fbq === 'function') {
      window.fbq('consent', 'grant');
    }
    return;
  }

  window.mutualysMetaPixelLoaded = true;
  !function initPixel(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function fbq() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', config.pixelId);
  window.fbq('consent', 'grant');
  // The server-side fbq.php snippet threads the form variant into
  // window.mutualysTrackingConfig.meta.variant so this PageView matches the
  // eager autoload path's tagging. Defaults to 'v1' so a missing config
  // (older cached template) still resolves to the control arm rather than
  // emitting a variant-less event.
  window.fbq('track', 'PageView', { variant: config.variant || 'v1' });
};

export const loadTrackingAfterConsent = () => {
  loadGoogleAnalyticsAfterConsent();
  loadMetaPixelAfterConsent();
};

export const applyConsentChoice = (choice, { persist = true } = {}) => {
  const previousConsent = normalizeConsentChoice(getStoredConsentChoice()) || 'pending';
  const normalizedChoice = normalizeConsentChoice(choice);
  const consentChoice = normalizedChoice || 'refused';

  if (persist) {
    setStoredConsentChoice(consentChoice);
  } else {
    mutualysConsentChoiceMemory = consentChoice;
  }

  if (consentChoice === 'accepted') {
    window.mutualysTrackingDisabled = false;
    loadTrackingAfterConsent();
  } else {
    disableTrackingForCurrentPage();
  }

  // The initial page_view samples consent before any choice is made, so a
  // first-time accept/refuse would otherwise never be recorded if the visitor
  // leaves without further form interaction. Emit the explicit transition so
  // the session's consent state is captured even with no further events.
  if (normalizedChoice && normalizedChoice !== previousConsent) {
    sendAnonymousEvent('consent_update', { cookie_consent: normalizedChoice });
  }
};

export const setupWebsiteEmbedConsentBridge = () => {
  if (!isConsentExcludedWebsiteEmbed()) return false;

  applyConsentChoice(getWebsiteEmbedConsentChoice(), { persist: false });

  if (!mutualysWebsiteEmbedConsentBridgeReady) {
    mutualysWebsiteEmbedConsentBridgeReady = true;

    window.addEventListener('message', (event) => {
      const data = event.data || {};

      if (
        typeof data !== 'object'
        || !['mutualys:tracking-consent', 'mutualys:cookie-consent'].includes(data.type)
      ) {
        return;
      }

      const consentChoice = normalizeConsentChoice(
        data.consent || data.choice || data.value || data.trackingConsent
      );

      if (!consentChoice) return;

      applyConsentChoice(consentChoice, { persist: false });
    });

    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'mutualys:consent-ready' }, '*');
    }
  }

  return true;
};

// Generic GA event sender. Gated on consent + a captured GA client id, and
// always authenticated with the dedicated `mutualys_ga_track` nonce (the action
// that wp_ajax_mutualys_ga_track / check_ajax_referer expects). Callers pass the
// event name plus any event-specific parameters; utm_* are merged in here.
export const sendGAEvent = (eventName, parameters = {}, { transport = 'fetch' } = {}) => {
  if (!hasTrackingConsent() || !window.mutualysGAClientId) return;

  const config = window.mutualysTrackingConfig?.ga;
  const urlParams = new URLSearchParams(window.location.search);
  const projectId = urlParams.get('project') || config?.projectId || '';
  if (!projectId) return;

  const body = new URLSearchParams({
    action: 'mutualys_ga_track',
    project_id: projectId,
    client_id: window.mutualysGAClientId,
    event_name: eventName,
    parameters: JSON.stringify(Object.assign({
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || ''
    }, parameters)),
    nonce: config?.nonce || ''
  });

  if (transport === 'beacon' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon('/wp-admin/admin-ajax.php', body);
    return;
  }

  fetch('/wp-admin/admin-ajax.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body
  }).catch(error => console.log('GA tracking error:', error));
};

const getAnonymousTrackingConfig = () => (
  (typeof window !== 'undefined' && window.mutualysAnonymousTrackingConfig) || {}
);

const getAnonymousTrackingUrl = () => {
  const config = getAnonymousTrackingConfig();
  return config.restUrl || '/wp-json/mutualys-plugin/v1/anonymous-events';
};

const getAnonymousTrackingNonce = () => {
  const config = getAnonymousTrackingConfig();
  return config.nonce || window.mutualysFormData?.nonce || window.mutualysPrivateFormData?.nonce || '';
};

export const getAnonymousSessionId = () => {
  if (mutualysAnonymousSessionId) return mutualysAnonymousSessionId;

  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    mutualysAnonymousSessionId = crypto.randomUUID().replace(/-/g, '');
    return mutualysAnonymousSessionId;
  }

  mutualysAnonymousSessionId = `mf${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return mutualysAnonymousSessionId;
};

export const getAnonymousDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';

  const width = window.innerWidth
    || document.documentElement?.clientWidth
    || window.screen?.width
    || 0;

  if (width > 0 && width < 768) return 'mobile';
  if (width > 0 && width < 1024) return 'tablet';

  return 'desktop';
};

export const getAnonymousCookieConsent = () => (
  normalizeConsentChoice(getStoredConsentChoice()) || 'pending'
);

const getAnonymousPagePath = () => {
  const path = window.location.pathname || '';
  return path.replace(/(\/form-private\/)[^/?#]+/i, '$1:redacted');
};

const anonymousEventPayload = (eventName, parameters = {}) => {
  const config = getAnonymousTrackingConfig();
  const urlParams = new URLSearchParams(window.location.search);

  // Resolve the experience dimensions with a fallback to the last event's
  // context, then remember them. This keeps context-less events (e.g. the
  // consent_update fired from the shared banner handler) attributed to the
  // same source/form so they survive the dashboard's dimension filters.
  const project = parameters.project || parameters.project_id || urlParams.get('project') || config.projectId || mutualysAnonymousContext.project || '';
  const source = parameters.source || mutualysAnonymousContext.source || '';
  const formType = parameters.form_type || mutualysAnonymousContext.form_type || '';
  const formVariant = parameters.form_variant || mutualysAnonymousContext.form_variant || '';
  mutualysAnonymousContext = { project, source, form_type: formType, form_variant: formVariant };

  return {
    session_id: getAnonymousSessionId(),
    event_name: eventName,
    project,
    source,
    form_type: formType,
    form_variant: formVariant,
    device_type: parameters.device_type || getAnonymousDeviceType(),
    cookie_consent: parameters.cookie_consent || getAnonymousCookieConsent(),
    step_name: parameters.step_name || parameters.completed_step || parameters.abandoned_step || '',
    step_number: parameters.step_number ?? '',
    elapsed_ms: parameters.elapsed_ms ?? parameters.time_on_step ?? '',
    error_type: parameters.error_type || '',
    reason: parameters.reason || '',
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    page_path: getAnonymousPagePath(),
  };
};

export const sendAnonymousEvent = (eventName, parameters = {}, { transport = 'fetch' } = {}) => {
  const config = getAnonymousTrackingConfig();
  if (config.enabled === false || typeof window === 'undefined') return;

  const event = anonymousEventPayload(eventName, parameters);
  if (!event.project) return;

  const body = new URLSearchParams({
    _wpnonce: getAnonymousTrackingNonce(),
    session_id: event.session_id,
    events: JSON.stringify([event]),
  });

  const url = getAnonymousTrackingUrl();
  if (transport === 'beacon' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    if (navigator.sendBeacon(url, body)) {
      return;
    }
  }

  if (typeof fetch === 'undefined') return;

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
    credentials: 'same-origin',
    keepalive: transport === 'beacon',
  }).catch(() => {});
};

export const trackAnonymousPageView = (parameters = {}) => {
  sendAnonymousEvent('page_view', parameters);
};

export const setupAnonymousFormStartTracking = (target, parameters = {}) => {
  if (!target || target.dataset?.anonymousStartTrackingBound === '1') return;

  if (target.dataset) {
    target.dataset.anonymousStartTrackingBound = '1';
  }

  trackAnonymousPageView(parameters);
  let anonymousFormStarted = false;

  const markStarted = () => {
    if (anonymousFormStarted) return;
    anonymousFormStarted = true;
    sendAnonymousEvent('form_start', parameters);
  };

  ['click', 'input', 'change', 'submit'].forEach((eventName) => {
    target.addEventListener(eventName, markStarted, { capture: true, passive: true });
  });
};
